import React from 'react'
import { Box, BoxProps, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'

export interface IconRailItem {
  id: string
  label: string
  icon: React.ReactNode
  disabled?: boolean
}

export interface IconRailProps extends Omit<BoxProps, 'children' | 'onSelect'> {
  items: IconRailItem[]
  activeId?: string
  onSelect: (id: string) => void
  /** Show the caption under each icon. Defaults to true. */
  showLabels?: boolean
}

const Root = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 'var(--ds-space-1)',
  width: '100%',
})

const Item = styled('button', {
  shouldForwardProp: (p) => p !== '$active',
})<{ $active?: boolean }>(({ $active }) => ({
  all: 'unset',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 5,
  padding: '10px 8px',
  borderRadius: 'var(--ds-radius-md)',
  cursor: 'pointer',
  width: '100%',
  boxSizing: 'border-box',
  background: $active ? 'var(--ds-hover)' : 'transparent',
  color: $active ? 'var(--ds-ink)' : 'var(--ds-ink-muted)',
  transition: `background var(--ds-duration-fast) var(--ds-ease), color var(--ds-duration-fast) var(--ds-ease)`,
  '&:hover': {
    background: 'var(--ds-hover)',
    color: 'var(--ds-ink)',
  },
  '&[disabled]': {
    opacity: 0.4,
    cursor: 'not-allowed',
    pointerEvents: 'none',
  },
  '&:focus-visible': {
    outline: '2px solid var(--ds-ink)',
    outlineOffset: 2,
  },
}))

const IconWrap = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 22,
  lineHeight: 1,
  '& svg': { fontSize: 22 },
})

const Label = styled(Typography)({
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  lineHeight: 1,
})

/** Vertical icon-only navigation rail — used for studio tool categories, shell sidebars */
export function IconRail({ items, activeId, onSelect, showLabels = true, ...rest }: IconRailProps) {
  return (
    <Root role='tablist' {...rest}>
      {items.map((item) => (
        <Item
          key={item.id}
          type='button'
          role='tab'
          $active={item.id === activeId}
          aria-selected={item.id === activeId}
          disabled={item.disabled}
          title={item.label}
          onClick={() => onSelect(item.id)}
        >
          <IconWrap aria-hidden>{item.icon}</IconWrap>
          {showLabels && <Label>{item.label}</Label>}
        </Item>
      ))}
    </Root>
  )
}
